import { useCallback, useState } from 'react';

export type UseDisclosureHandlers<Payload = undefined> = {
  open: undefined extends Payload ? () => void : (payload?: Payload) => void;
  close: () => void;
  toggle: () => void;
  set: (value: boolean) => void;
};

export const useDisclosure = <Payload = undefined>(
  initialState: boolean = false,
) => {
  const [opened, setOpened] = useState(initialState);
  const [payload, setPayload] = useState<Payload | undefined>(undefined);

  const open = useCallback((next?: Payload) => {
    setPayload(next);
    setOpened(true);
  }, []) as UseDisclosureHandlers<Payload>['open'];

  const close = useCallback(() => {
    setOpened(false);
    setPayload(undefined);
  }, []);

  const toggle = useCallback(() => {
    setOpened(prev => !prev);
  }, []);

  const set = useCallback((value: boolean) => {
    setOpened(value);
  }, []);

  const handlers: UseDisclosureHandlers<Payload> = {
    open,
    close,
    toggle,
    set,
  };

  return [opened, handlers, payload] as const;
};
